import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useEffect, useState } from "react";
import { RateType } from "store/queries/contractFeeSchedules";

/**
 * `ratePercent` is stored as a percentage for `percent` rows and as *cents per
 * unit* for `unit` rows — the input shows dollars, so unit values are scaled by
 * 100 on the way in and out. Mirrors the Add-rate form above the table.
 */
export const formatRateLabel = (
  ratePercent: number,
  rateType: RateType
): string =>
  rateType === "unit"
    ? `$${(ratePercent / 100).toFixed(2)} / unit`
    : `${ratePercent}%`;

interface IRateCellProps {
  ratePercent: number;
  rateType: RateType;
  editing: boolean;
  /** Hides the pencil for rows whose rate can't be corrected in place. */
  editable?: boolean;
  onBeginEdit: () => void;
  onCancel: () => void;
  onSave: (ratePercent: number, rateType: RateType) => void;
}

/**
 * CLE-2573 — the Rate cell of the rate calculations table. Reads as plain text
 * until the pencil is clicked, then becomes a type + amount editor so a
 * mistyped unit price or percentage can be corrected without removing and
 * re-attaching the file.
 */
export default function RateCell({
  ratePercent,
  rateType,
  editing,
  editable = true,
  onBeginEdit,
  onCancel,
  onSave,
}: IRateCellProps) {
  const [draftType, setDraftType] = useState<RateType>(rateType);
  const [draftAmount, setDraftAmount] = useState<number>(ratePercent);

  // Re-seed the draft each time this row enters edit mode, so a cancelled edit
  // (or an edit on a different row) never leaks into the next one.
  useEffect(() => {
    if (editing) {
      setDraftType(rateType);
      setDraftAmount(ratePercent);
    }
  }, [editing, rateType, ratePercent]);

  if (!editing) {
    return (
      <div className="flex align-items-center gap-1">
        <span>{formatRateLabel(ratePercent, rateType)}</span>
        {editable && (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text p-button-sm"
            tooltip="Edit rate"
            tooltipOptions={{ position: "top" }}
            aria-label="Edit rate"
            onClick={onBeginEdit}
          />
        )}
      </div>
    );
  }

  const isUnit = draftType === "unit";
  const commit = () => onSave(draftAmount, draftType);

  return (
    <div className="flex align-items-center gap-1">
      <Dropdown
        value={draftType}
        options={[
          { label: "$", value: "unit" },
          { label: "%", value: "percent" },
        ]}
        onChange={(e) => {
          // Switching type changes the unit of the amount, so reset rather than
          // reinterpret 12.50%/unit as $12.50/unit.
          setDraftType((e.value ?? "percent") as RateType);
          setDraftAmount(100);
        }}
        style={{ width: "70px" }}
        aria-label="Rate type"
      />
      <InputNumber
        value={isUnit ? draftAmount / 100 : draftAmount}
        onValueChange={(e) =>
          setDraftAmount(isUnit ? (e.value || 0) * 100 : e.value || 0)
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") onCancel();
        }}
        mode="decimal"
        min={0}
        max={100000}
        minFractionDigits={0}
        maxFractionDigits={isUnit ? 4 : 2}
        inputStyle={{ width: "90px" }}
        aria-label={isUnit ? "Price per unit" : "Rate percentage"}
      />
      <Button
        icon="pi pi-check"
        className="p-button-rounded p-button-text p-button-sm p-button-success"
        tooltip="Save rate"
        tooltipOptions={{ position: "top" }}
        aria-label="Save rate"
        onClick={commit}
      />
      <Button
        icon="pi pi-times"
        className="p-button-rounded p-button-text p-button-sm"
        tooltip="Cancel"
        tooltipOptions={{ position: "top" }}
        aria-label="Cancel rate edit"
        onClick={onCancel}
      />
    </div>
  );
}
